import { AddDiv, CreateDomElement } from '../engine/viewer/domutils.js';
import { ButtonDialog } from './dialog.js';
import { Loc } from '../engine/core/localization.js';

// Every field is built the same way, so that a label, an input and the error
// slot under it cannot drift apart between the three dialogs.
function AddField (parentDiv, labelText, type, autocomplete)
{
    AddDiv (parentDiv, 'ov_auth_label', labelText);
    let input = CreateDomElement ('input', 'ov_auth_input');
    input.setAttribute ('type', type);
    input.setAttribute ('autocomplete', autocomplete);
    parentDiv.appendChild (input);
    return input;
}

function AddMessageSlot (parentDiv)
{
    let messageDiv = AddDiv (parentDiv, 'ov_auth_message');
    messageDiv.style.display = 'none';
    return {
        Show (text, isError) {
            messageDiv.innerText = text;
            messageDiv.classList.toggle ('error', isError === true);
            messageDiv.style.display = 'block';
        },
        Hide () {
            messageDiv.style.display = 'none';
        }
    };
}

// Enter should submit a form of two fields; without this the dialog looks
// like a form but behaves like a page.
function SubmitOnEnter (inputs, submit)
{
    for (let input of inputs) {
        input.addEventListener ('keydown', (ev) => {
            if (ev.key === 'Enter') {
                ev.preventDefault ();
                submit ();
            }
        });
    }
}

export function ShowAuthDialog (client, mode, onSignedIn, presetEmail)
{
    let isRegister = (mode === 'register');
    let dialog = new ButtonDialog ();
    let busy = false;

    let title = isRegister ? Loc ('Create account') : Loc ('Sign in');
    let contentDiv = dialog.Init (title, [
        {
            name : Loc ('Cancel'),
            subClass : 'outline',
            onClick () {
                dialog.Close ();
            }
        },
        {
            name : title,
            onClick () {
                Submit ();
            }
        }
    ]);
    dialog.GetContentDiv ().classList.add ('ov_auth_dialog');

    let emailInput = AddField (contentDiv, Loc ('Email'), 'email', 'username');
    let passwordInput = AddField (
        contentDiv,
        Loc ('Password'),
        'password',
        isRegister ? 'new-password' : 'current-password'
    );
    if (isRegister) {
        AddDiv (contentDiv, 'ov_auth_hint', Loc ('At least 10 characters.'));
    }
    let message = AddMessageSlot (contentDiv);

    let switchDiv = AddDiv (contentDiv, 'ov_auth_switch');
    let switchLink = AddDiv (switchDiv, 'ov_auth_link', isRegister
        ? Loc ('Already have an account? Sign in')
        : Loc ('No account yet? Create one'));
    switchLink.addEventListener ('click', () => {
        let typedEmail = emailInput.value;
        dialog.Close ();
        ShowAuthDialog (client, isRegister ? 'login' : 'register', onSignedIn, typedEmail);
    });

    // What an account actually gives, in the numbers of the free plan. This
    // line used to say storage was still to come — it is here now.
    AddDiv (contentDiv, 'ov_auth_note', Loc ('An account keeps your models on the server: 1 GB free, a scene up to 300 MB, stored for a week.'));

    async function Submit ()
    {
        if (busy) {
            return;
        }
        message.Hide ();
        busy = true;
        try {
            let user = isRegister
                ? await client.Register (emailInput.value, passwordInput.value)
                : await client.Login (emailInput.value, passwordInput.value);
            dialog.Close ();
            if (onSignedIn) {
                onSignedIn (user);
            }
        } catch (error) {
            message.Show (error.message, true);
            passwordInput.focus ();
        } finally {
            busy = false;
        }
    }

    SubmitOnEnter ([emailInput, passwordInput], Submit);

    dialog.Open ();
    if (presetEmail) {
        emailInput.value = presetEmail;
        passwordInput.focus ();
    } else {
        emailInput.focus ();
    }
    return dialog;
}

export function ShowAccountDialog (client, callbacks)
{
    let dialog = new ButtonDialog ();
    let contentDiv = dialog.Init (Loc ('Account'), [
        {
            name : Loc ('Close'),
            subClass : 'outline',
            onClick () {
                dialog.Close ();
            }
        },
        {
            name : Loc ('Sign out'),
            async onClick () {
                try {
                    await client.Logout ();
                } catch {
                    // A session that cannot be closed on the server is still
                    // worth forgetting here; the cookie expires by itself.
                }
                dialog.Close ();
                if (callbacks && callbacks.onSignedOut) {
                    callbacks.onSignedOut ();
                }
            }
        }
    ]);
    dialog.GetContentDiv ().classList.add ('ov_auth_dialog');

    AddDiv (contentDiv, 'ov_auth_email', client.user.email);
    // The plan comes from the account itself rather than being written in:
    // after a payment this line has to say something different.
    AddDiv (contentDiv, 'ov_auth_plan',
        client.user.plan === 'free' ? Loc ('Free plan') : Loc ('Subscription'));

    let message = AddMessageSlot (contentDiv);

    let actionsDiv = AddDiv (contentDiv, 'ov_auth_actions');
    if (callbacks && callbacks.onShowBilling) {
        let billingLink = AddDiv (actionsDiv, 'ov_auth_link', Loc ('Subscription and payment'));
        billingLink.addEventListener ('click', () => {
            dialog.Close ();
            callbacks.onShowBilling ();
        });
    }
    let changeLink = AddDiv (actionsDiv, 'ov_auth_link', Loc ('Change password'));
    changeLink.addEventListener ('click', () => {
        dialog.Close ();
        ShowChangePasswordDialog (client);
    });

    let elsewhereLink = AddDiv (actionsDiv, 'ov_auth_link', Loc ('Sign out on other devices'));
    elsewhereLink.addEventListener ('click', async () => {
        try {
            let result = await client.LogoutEverywhereElse ();
            message.Show (result.revokedSessions > 0
                ? Loc ('Other sessions are closed.')
                : Loc ('There were no other sessions.'), false);
        } catch (error) {
            message.Show (error.message, true);
        }
    });

    dialog.Open ();
    return dialog;
}

export function ShowChangePasswordDialog (client)
{
    let dialog = new ButtonDialog ();
    let busy = false;

    let contentDiv = dialog.Init (Loc ('Change password'), [
        {
            name : Loc ('Cancel'),
            subClass : 'outline',
            onClick () {
                dialog.Close ();
            }
        },
        {
            name : Loc ('Save'),
            onClick () {
                Submit ();
            }
        }
    ]);
    dialog.GetContentDiv ().classList.add ('ov_auth_dialog');

    let currentInput = AddField (contentDiv, Loc ('Current password'), 'password', 'current-password');
    let nextInput = AddField (contentDiv, Loc ('New password'), 'password', 'new-password');
    let repeatInput = AddField (contentDiv, Loc ('Repeat new password'), 'password', 'new-password');
    let message = AddMessageSlot (contentDiv);

    async function Submit ()
    {
        if (busy) {
            return;
        }
        message.Hide ();
        // Checked here rather than on the server: the server never sees the
        // repeated field, and this is a typo guard, not a rule.
        if (nextInput.value !== repeatInput.value) {
            message.Show (Loc ('The passwords do not match.'), true);
            return;
        }
        busy = true;
        try {
            let result = await client.ChangePassword (currentInput.value, nextInput.value);
            message.Show (result.revokedSessions > 0
                ? Loc ('Password changed, other sessions are closed.')
                : Loc ('Password changed.'), false);
            currentInput.value = '';
            nextInput.value = '';
            repeatInput.value = '';
        } catch (error) {
            message.Show (error.message, true);
        } finally {
            busy = false;
        }
    }

    SubmitOnEnter ([currentInput, nextInput, repeatInput], Submit);

    dialog.Open ();
    currentInput.focus ();
    return dialog;
}
